import React, { useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const REVERT_KEY = "sf_admin_email_revert";

export default function AdminEmailPanel() {
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(sessionStorage.getItem(REVERT_KEY) ? "done" : "request");
  const [form, setForm] = useState({ new_email: "", current_password: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const run = async (fn) => {
    setError("");
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Request failed.");
    } finally {
      setBusy(false);
    }
  };

  const request = (e) => {
    e.preventDefault();
    run(async () => {
      await api.post("/admin/account/email/request", { new_email: form.new_email, current_password: form.current_password });
      setForm((f) => ({ ...f, current_password: "" }));
      setStep("confirm");
      toast.success(`Code sent to ${form.new_email}`);
    });
  };

  const confirm = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await api.post("/admin/account/email/confirm", { code: form.code });
      sessionStorage.setItem(REVERT_KEY, JSON.stringify({ token: data.revert_token, until: Date.now() + 30 * 60000 }));
      setStep("done");
      await refresh?.();
      toast.success(`Admin email changed to ${data.email}`);
    });
  };

  const revert = () =>
    run(async () => {
      const saved = JSON.parse(sessionStorage.getItem(REVERT_KEY) || "{}");
      const { data } = await api.post("/admin/account/email/revert", { revert_token: saved.token });
      sessionStorage.removeItem(REVERT_KEY);
      setStep("request");
      await refresh?.();
      toast.success(`Reverted to ${data.email}`);
    });

  return (
    <div className="mt-8 rounded-xl border border-ink-100 bg-white p-6 shadow-sm" data-testid="admin-email-panel">
      <div className="flex items-center gap-2">
        <Mail className="h-5 w-5 text-brand" />
        <h3 className="h3">Change admin email</h3>
      </div>
      <p className="mt-1 text-sm text-ink-500" data-testid="admin-email-current">
        Current: {user?.email}. A 6-digit code is sent to the new address only (expires in 10 minutes).
      </p>
      {step === "request" && (
        <form onSubmit={request} className="mt-4 grid gap-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="ae-new">New admin email</Label>
            <Input id="ae-new" type="email" required value={form.new_email} onChange={set("new_email")} data-testid="admin-email-new-input" />
          </div>
          <div>
            <Label htmlFor="ae-pw">Current password</Label>
            <Input id="ae-pw" type="password" autoComplete="current-password" required value={form.current_password} onChange={set("current_password")} data-testid="admin-email-password-input" />
          </div>
          <div className="flex items-end">
            <Button type="submit" disabled={busy} data-testid="admin-email-send-code-btn">{busy ? "Sending…" : "Send code"}</Button>
          </div>
        </form>
      )}
      {step === "confirm" && (
        <form onSubmit={confirm} className="mt-4 flex flex-wrap items-end gap-4">
          <div>
            <Label htmlFor="ae-code">Code from {form.new_email}</Label>
            <Input id="ae-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={form.code} onChange={set("code")} data-testid="admin-email-code-input" />
          </div>
          <Button type="submit" disabled={busy} data-testid="admin-email-confirm-btn">{busy ? "Confirming…" : "Confirm change"}</Button>
          <Button type="button" variant="ghost" onClick={() => { setError(""); setStep("request"); }} data-testid="admin-email-cancel-btn">Cancel</Button>
        </form>
      )}
      {step === "done" && (
        <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="admin-email-done">
          <p className="text-sm">Changed. Test the new login in a private window. Keep this tab open until it works.</p>
          <Button type="button" variant="outline" disabled={busy} onClick={revert} data-testid="admin-email-revert-btn">Undo change (30 min)</Button>
        </div>
      )}
      {error && <p className="mt-3 text-sm text-red-600" data-testid="admin-email-error">{error}</p>}
    </div>
  );
}
